import { Form, Select } from 'antd';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { DateRangePicker } from 'src/components/DateRangePicker/DateRangePicker';
import { SearchPanel } from 'src/components/SearchPanel/SearchPanel';
import {
    SearchPanelFilterForm,
    SearchPanelFilterFormProps,
} from 'src/components/SearchPanel/SearchPanelFilterForm';
import { getTransactionStatuses } from 'src/pages/TransactionsPage/TransactionStatusInfoMap';
import {
    TransactionsFilter,
    TransactionsStore,
} from 'src/stores/TransactionsStore/TransactionsStore';
import { TransactionsTypes } from 'src/stores/TransactionsStore/TransactionsStore.types';
import { DateHelper } from 'src/utils/DateHelper';
import { FormHelper } from 'src/utils/FormHelper';
import styled from 'styled-components';

export const TransactionsHeaderPanel = observer(() => {
    const { t } = useTranslation();

    return (
        <SearchPanel
            inputSearchFormName={formNames.getPath('filterSearch')}
            store={store}
            renderPanel={(props) => <FilterPanel {...props} />}
            inputSearchPlaceholder={t(
                'Page.Transactions.AsideHeader.Search.Placeholder',
            )}
        />
    );
});

const FilterPanel = observer((props: SearchPanelFilterFormProps) => {
    const statuses = getTransactionStatuses();

    const { t } = useTranslation();

    return (
        <SearchPanelFilterForm
            {...props}
            onFinish={(values: FilterValuesWithDateRanges) => {
                store.pager.reset();
                store.filterCriteria?.applyFilter(
                    convertFormValuesToFilterCriteria(values),
                );
                store.loadList();
            }}
        >
            <StyledSelect
                label={t('Page.Transactions.Filter.Status')}
                name={formNames.getPath('filterStatuses')}
            >
                <Select mode="multiple" allowClear data-testid="filterStatuses">
                    {statuses.map((status) => (
                        <Select.Option
                            key={status.id}
                            value={status.id}
                            data-testid={status.id}
                        >
                            {t(`data:status.transaction.${status.id}`)}
                        </Select.Option>
                    ))}
                </Select>
            </StyledSelect>

            <StyledSelect
                label={t('Page.Transactions.Filter.Type')}
                name={formNames.getPath('filterTypes')}
            >
                <Select mode="multiple" allowClear data-testid="filterTypes">
                    <Select.Option
                        value={TransactionsTypes.Repayment}
                        data-testid={TransactionsTypes.Repayment}
                    >
                        {t('Page.Transactions.Filter.Type.Repayment')}
                    </Select.Option>
                    <Select.Option
                        value={TransactionsTypes.Disbursement}
                        data-testid={TransactionsTypes.Disbursement}
                    >
                        {t('Page.Transactions.Filter.Type.Disbursement')}
                    </Select.Option>
                </Select>
            </StyledSelect>
            <Form.Item
                label={t('Page.Transactions.Filter.Type.CreatedAt')}
                name={formNames.getPath(createdAtDateRangeName)}
            >
                <DateRangePicker data-testid={createdAtDateRangeName} />
            </Form.Item>
            <Form.Item
                label={t('Page.Transactions.Filter.Type.ProcessedAt')}
                name={formNames.getPath(processedAtDateRangeName)}
            >
                <DateRangePicker data-testid={processedAtDateRangeName} />
            </Form.Item>
        </SearchPanelFilterForm>
    );
});

const store = TransactionsStore;
const formNames = FormHelper.createNameProvider<FilterValuesWithDateRanges>();
const createdAtDateRangeName = 'filterCreatedAt';
const processedAtDateRangeName = 'filterProcessedAt';

type FilterValuesWithDateRanges = Omit<
    TransactionsFilter,
    | 'filterCreatedAtFrom'
    | 'filterCreatedAtTo'
    | 'filterProcessedAtFrom'
    | 'filterProcessedAtTo'
> & {
    [createdAtDateRangeName]: [moment.Moment, moment.Moment];
    [processedAtDateRangeName]: [moment.Moment, moment.Moment];
};

const convertFormValuesToFilterCriteria = (
    values: FilterValuesWithDateRanges,
): TransactionsFilter => {
    const { filterCreatedAt, filterProcessedAt, ...otherValues } = values;
    const [filterCreatedAtFrom, filterCreatedAtTo] = filterCreatedAt || [
        undefined,
        undefined,
    ];
    filterCreatedAtFrom?.set('hour', 0).set('minute', 0).set('second', 0);
    filterCreatedAtTo?.set('hour', 23).set('minute', 59).set('second', 59);
    const [filterProcessedAtFrom, filterProcessedAtTo] = filterProcessedAt || [
        undefined,
        undefined,
    ];
    filterProcessedAtFrom?.set('hour', 0).set('minute', 0).set('second', 0);
    filterProcessedAtTo?.set('hour', 23).set('minute', 59).set('second', 59);

    return {
        ...otherValues,
        filterProcessedAtFrom: filterProcessedAtFrom
            ? DateHelper.formatToISO(filterProcessedAtFrom.startOf('day'))
            : undefined,
        filterProcessedAtTo: filterProcessedAtTo
            ? DateHelper.formatToISO(filterProcessedAtTo.endOf('day'))
            : undefined,
        filterCreatedAtFrom: filterCreatedAtFrom
            ? DateHelper.formatToISO(filterCreatedAtFrom.startOf('day'))
            : undefined,
        filterCreatedAtTo: filterCreatedAtTo
            ? DateHelper.formatToISO(filterCreatedAtTo.endOf('day'))
            : undefined,
    };
};

const StyledSelect = styled(Form.Item)`
    .ant-select:not(.ant-select-customize-input) .ant-select-selector {
        display: flex;
        align-items: center;
        min-height: 2.7rem;
        border-radius: 0.5rem;
    }
`;
